import { ReactNode } from 'react';
import { Box, GDSStyleObject } from '@leagueplatform/genesis-core';
import { BasePage } from 'components/base-page/base-page.component';
import { useIsOnboardingMobile } from 'hooks/use-is-onboarding-mobile/use-is-onboarding-mobile.hook';

// Figma's card width (node 6120-195378). Genesis sets `html { font-size: 10px }`
// so rem resolves at 10px/rem, not the browser default 16px/rem — this goes in
// `style` as raw px to land on the spec width.
const CARD_WIDTH_PX = 708;

type OnboardingCardProps = {
  children: ReactNode;
  /**
   * Render the card chrome (surface, shadow, radius). The Plan Switcher gate
   * passes `false` until its server-driven title arrives so members who are
   * auto-resolved straight past the gate never see an empty card flash.
   */
  hasCardChrome?: boolean;
  /** Extra styles for the full-viewport surface behind the card. */
  surfaceCss?: GDSStyleObject;
  /** Extra styles for the card itself. */
  css?: GDSStyleObject;
};

/**
 * The shared shell every post-auth onboarding screen sits in: a full-viewport
 * centered surface holding a 708px card.
 *
 * From 641px the card floats — white on the secondary grey, with the
 * `$card` shadow and `$medium` radius. On mobile it goes full-bleed: the
 * page gutter and the card chrome both come off and the surface behind takes
 * the card's own colour, so the card reads as a plain edge-to-edge white
 * screen per the responsive Figma. Tracking the *card* token for the surface
 * (rather than a page token) keeps the seam invisible if the card surface ever
 * changes.
 *
 * Each screen supplies its own content padding — the shell deliberately owns
 * no inner padding, because the flow's screens legitimately differ there.
 */
export const OnboardingCard = ({
  children,
  hasCardChrome = true,
  surfaceCss,
  css,
}: OnboardingCardProps) => {
  const isOnboardingMobile = useIsOnboardingMobile();

  return (
    <BasePage
      isPrivatePage={false}
      css={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: isOnboardingMobile
          ? '$surfaceCardSecondary'
          : '$surfaceBackgroundSecondary',
        // Figma's mobile frame has the card fill the viewport edge-to-edge. A
        // fixed gutter at every breakpoint ate into the card's width on
        // mobile, forcing text wraps the mobile frame doesn't have.
        padding: isOnboardingMobile ? '$none' : '$two',
        ...surfaceCss,
      }}
    >
      <Box
        style={{
          width: isOnboardingMobile ? '100%' : `${CARD_WIDTH_PX}px`,
          maxWidth: '100%',
          // Overrides the flex-item default of `min-width: auto`. Without it
          // the card refuses to shrink below its content's intrinsic
          // min-content width and overflows on narrow (~390px) viewports
          // instead of honouring maxWidth and wrapping.
          minWidth: 0,
          marginInline: 'auto',
        }}
        css={{
          ...(hasCardChrome && {
            background: '$surfaceCardSecondary',
            // Full-bleed on mobile, so the floating-card chrome comes off:
            // with no gutter for the shadow to fall into it can only bleed
            // vertically, banding the card against the page behind it.
            boxShadow: isOnboardingMobile ? 'none' : '$card',
            borderRadius: isOnboardingMobile ? '$none' : '$medium',
          }),
          ...css,
        }}
      >
        {children}
      </Box>
    </BasePage>
  );
};
